/**
 * The character builder's wire shapes (ADR-090): what a campaign says about the
 * characters it admits, and what a player's choices look like when they reach the
 * pod. The pod resolves every number itself from the SRD catalog
 * (`@everwake/character-builder`); the client sends choices, never a sheet.
 */
import { z } from 'zod';
import { AbilityId, SkillId } from './primitives.js';
/** How the six scores are set: the SRD's two methods. A pregen's are fixed by the pack. */
export const AbilityMethod = z.enum(['point_buy', 'standard_array']);
/** Whether a campaign hands the player a pregen, lets them build, or offers both. */
export const CharacterMode = z.enum(['pregen', 'custom', 'either']);
/** The launch lists (ADR-068, architecture §5.1's MVP content subset). */
export const LAUNCH_SPECIES = ['human', 'dwarf', 'elf', 'halfling'];
export const LAUNCH_CLASSES = ['fighter', 'rogue', 'cleric', 'wizard'];
export const LAUNCH_BACKGROUNDS = ['acolyte', 'criminal', 'sage', 'soldier'];
export const PregenCard = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    blurb: z.string().default(''),
});
/**
 * What the campaign's author decided about the character: the mode, the level,
 * which methods, species, classes and backgrounds are open, and the shop's budget.
 * Authored in `campaign.yaml` under `character:`; every field has the launch default.
 */
export const CampaignCharacterRules = z.object({
    mode: CharacterMode.default('either'),
    /** ADR-068: levels 1–3 at launch. */
    level: z.number().int().min(1).max(3).default(1),
    abilityMethods: z.array(AbilityMethod).min(1).default(['point_buy', 'standard_array']),
    species: z.array(z.string().min(1)).min(1).default([...LAUNCH_SPECIES]),
    classes: z.array(z.string().min(1)).min(1).default([...LAUNCH_CLASSES]),
    backgrounds: z.array(z.string().min(1)).min(1).default([...LAUNCH_BACKGROUNDS]),
    /**
     * The shop's budget when the player buys instead of taking the kits: the SRD's
     * gold option of the class plus the background's, or a sum the campaign fixes.
     */
    startingGold: z.union([z.literal('by_class'), z.object({ gp: z.number().int().min(0).max(5000) })]).default('by_class'),
    /** The pack's ready-made characters, when it has any; the first is the default. */
    pregens: z.array(PregenCard).default([]),
    /**
     * ADR-091 as the founder corrected it (2026-09-08): levels are earned only by
     * completing the campaign's milestones — a quest stage the author names — never
     * by sessions played. Reaching `stage` of `quest` raises the character to `level`
     * there and then; a player who wanders for ten sittings without touching the
     * plot stays where they started.
     */
    milestones: z.array(z.object({ quest: z.string().min(1), stage: z.string().min(1), level: z.number().int().min(2).max(3) })).default([]),
});
/** ADR-093: what a spellcaster chooses — by the SRD's tables, checked by the pod against the catalog. */
export const SpellChoices = z.object({
    cantrips: z.array(z.string().min(1)).max(6).default([]),
    prepared: z.array(z.string().min(1)).max(10).default([]),
    /** Magic Initiate: a class list, two of its cantrips and one of its first-level spells. */
    initiate: z.object({ classId: z.enum(['cleric', 'druid', 'wizard']), cantrips: z.array(z.string().min(1)).length(2), spell: z.string().min(1) }).optional(),
});
/** Base scores before the background's bonus: 8–15 by point buy, the array's values otherwise. */
export const BaseAbilities = z.object({
    str: z.number().int().min(3).max(18),
    dex: z.number().int().min(3).max(18),
    con: z.number().int().min(3).max(18),
    int: z.number().int().min(3).max(18),
    wis: z.number().int().min(3).max(18),
    cha: z.number().int().min(3).max(18),
});
export const Purchase = z.object({
    itemId: z.string().min(1),
    qty: z.number().int().min(1).max(50),
});
/** The SRD's choice: the class and background kits, or their gold spent in the shop. */
export const EquipmentChoice = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('kit'), classKit: z.string().min(1).default('A') }),
    z.object({ mode: z.literal('shop'), purchases: z.array(Purchase).max(80).default([]) }),
]);
/** A player's choices; the pod resolves them into a character or answers with issues. */
export const CharacterBuild = z.object({
    name: z.string().trim().min(1).max(40),
    pronouns: z.string().trim().max(24).optional(),
    speciesId: z.string().min(1),
    lineageId: z.string().min(1).optional(),
    backgroundId: z.string().min(1),
    classId: z.string().min(1),
    abilityMethod: AbilityMethod,
    abilities: BaseAbilities,
    /** The background's +2/+1 (two abilities) or +1/+1/+1, on abilities the background names. */
    bonuses: z.record(AbilityId, z.union([z.literal(1), z.literal(2)])).default({}),
    /** Every skill chosen: the class's picks, plus a human's Skillful and an elf's Keen Senses (and Skilled's three). */
    skills: z.array(SkillId).max(11).default([]),
    /** ADR-091: the origin feat a human's Versatile grants; only a species with that trait sends one. */
    featId: z.string().min(1).optional(),
    /** ADR-093: a caster's cantrips and prepared spells, and Magic Initiate's picks when the feat is taken. */
    spells: SpellChoices.optional(),
    equipment: EquipmentChoice,
});
/** One thing the pod refuses about a build, with where it points. */
export const BuildIssue = z.object({ path: z.string(), message: z.string() });
//# sourceMappingURL=builder.js.map